import { lazy, Suspense } from "react";
import { Navigate, RouteObject } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/components/layouts";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { ProjectProvider } from "@/context/ProjectContext";
import { ProjectPages } from "@/pages/projects/ProjectPages";
import BillingPage from "@/pages/billing/BillingPage";
import Checkout from "@/pages/mySubscriptions/Checkout";
import Invoice from "@/pages/billing/Invoice";
import SubscriptionsPage from "@/pages/mySubscriptions/SubscriptionsPage";
import ThankYouPage from "@/pages/mySubscriptions/thankyouPage";
import SourceMedium from "@/pages/Analytics/source_medium";
import MfaVerify from "@/pages/auth/MfaVerify";

/* ================= Loader ================= */
const PageLoader = () => (
  <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>
);

/* ================= Lazy pages (DEFAULT EXPORTS ONLY) ================= */
const Login = lazy(() => import("@/pages/auth/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Projects = lazy(() => import("@/pages/projects/Projects"));
const ProjectTemplate = lazy(() => import("@/pages/projects/ProjectTemplate"));
const Media = lazy(() => import("@/pages/media/MediaPage"));
const QrPage = lazy(() => import("@/pages/Qr/QrPage"));
const QrCreate = lazy(() => import("@/pages/Qr/QrCreate"));
const QrCodes = lazy(() => import("@/pages/Qr/QrCodes"));
const ProjectViewer = lazy(() => import("@/pages/ProjectViewer"));
const TemplateViewer = lazy(() => import("@/pages/TeamplateViewer"));
const Index = lazy(() => import("@/pages/Index"));
const NotFound = lazy(() => import("@/pages/NotFound"));

/* ===== Analytics ===== */
const Insights = lazy(() => import("@/pages/Analytics/Insights"));
const QRScanAnalytics = lazy(() => import("@/pages/Analytics/QRScans"));
const Demographics = lazy(() => import("@/pages/Analytics/Demographics"));
const Device = lazy(() => import("@/pages/Analytics/Device"));
const MyFormsPage = lazy(
  () => import("@/pages/Analytics/Engagements/MyFormsPage"),
);
const MyPageResponses = lazy(
  () => import("@/pages/Analytics/Engagements/ResponsesPage"),
);

/* ===== Billing / Profile ===== */

const MyProfilePage = lazy(() => import("@/pages/Profile/MyProfilePage"));
const ProductPage = lazy(() => import("@/pages/Products/ProductPage"));

/* ===== Auth (non-lazy is fine too, but lazy is ok now) ===== */
const Register = lazy(() => import("@/pages/auth/Register"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));

/* ================= Suspense wrapper ================= */
const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/* ================= Protected Route (NO Suspense here) ================= */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
};

/* ================= ROUTES ================= */
export const routes: RouteObject[] = [
  /* ---------- AUTH ---------- */
  {
    element: <AuthLayout />,
    children: [
      {
        path: "/login",
        element: (
          <Lazy>
            <Login />
          </Lazy>
        ),
      },
      {
        path: "/mfa-verify",
        element: (
          <Lazy>
            <MfaVerify />
          </Lazy>
        ),
      },
      {
        path: "/signup",
        element: (
          <Lazy>
            <Register />
          </Lazy>
        ),
      },
      {
        path: "/forgot_password",
        element: (
          <Lazy>
            <ForgotPassword />
          </Lazy>
        ),
      },
      {
        path: "/reset-password",
        element: (
          <Lazy>
            <ResetPassword />
          </Lazy>
        ),
      },
    ],
  },

  /* ---------- PUBLIC ---------- */
  {
    path: "/view/:slug",
    element: (
      <Lazy>
        <ProjectViewer />
      </Lazy>
    ),
  },

  {
    path: "/preview/:id",
    element: (
      <Lazy>
        <TemplateViewer />
      </Lazy>
    ),
  },

  /* ---------- DASHBOARD (PROTECTED) ---------- */
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      /* Home */
      {
        index: true,
        element: (
          <Lazy>
            <Dashboard />
          </Lazy>
        ),
      },

      /* Media */
      {
        path: "media",
        element: (
          <Lazy>
            <Media />
          </Lazy>
        ),
      },

      /* Projects */
      {
        path: "Projects",
        element: (
          <Lazy>
            <Projects />
          </Lazy>
        ),
      },
      {
        path: "projectTemplate/:projectId/",
        element: (
          <Lazy>
            <ProjectTemplate />
          </Lazy>
        ),
      },
      {
        path: "projects/:projectId/pages",
        element: (
          <Lazy>
            <ProjectPages />
          </Lazy>
        ),
      },

      /* QR */
      {
        path: "qrpage",
        element: (
          <Lazy>
            <QrPage />
          </Lazy>
        ),
      },

      {
        path: "qrcreate",
        element: (
          <Lazy>
            <QrCreate />
          </Lazy>
        ),
      },
      {
        path: "qr-codes",
        element: (
          <Lazy>
            <QrCodes />
          </Lazy>
        ),
      },

      /* Billing */
      {
        path: "billing",
        element: (
          <Lazy>
            <BillingPage />
          </Lazy>
        ),
      },

      {
        path: "checkout",
        element: (
          <Lazy>
            <Checkout />
          </Lazy>
        ),
      },
      {
        path: "billing/invoice-details/:billing_id",
        element: (
          <Lazy>
            <Invoice />
          </Lazy>
        ),
      },

      /* Profile / Subscription */
      {
        path: "my-profile",
        element: (
          <Lazy>
            <MyProfilePage />
          </Lazy>
        ),
      },
      {
        path: "my-subscription",
        element: (
          <Lazy>
            <SubscriptionsPage />
          </Lazy>
        ),
      },
      {
        path: "thankyou",
        element: (
          <Lazy>
            <ThankYouPage />
          </Lazy>
        ),
      },

      /* Products */
      {
        path: "products",
        element: (
          <Lazy>
            <ProductPage />
          </Lazy>
        ),
      },

      /* Submissions */
      {
        path: "my-submissions",
        element: (
          <Lazy>
            <MyFormsPage />
          </Lazy>
        ),
      },
      {
        path: "my-submissions/page-responses/:id",
        element: (
          <Lazy>
            <MyPageResponses />
          </Lazy>
        ),
      },

      /* Analytics */
      {
        path: "analytics/insights",
        element: (
          <Lazy>
            <Insights />
          </Lazy>
        ),
      },

      {
        path: "analytics/sourcemedium",
        element: (
          <Lazy>
            <SourceMedium />
          </Lazy>
        ),
      },
      {
        path: "analytics/qr",
        element: (
          <Lazy>
            <QRScanAnalytics />
          </Lazy>
        ),
      },
      {
        path: "analytics/demographic",
        element: (
          <Lazy>
            <Demographics />
          </Lazy>
        ),
      },
      {
        path: "analytics/device",
        element: (
          <Lazy>
            <Device />
          </Lazy>
        ),
      },
    ],
  },

  /* ---------- BUILDER ---------- */
  {
    path: "/builder",
    element: (
      <ProtectedRoute>
        <ProjectProvider>
          <Lazy>
            <Index />
          </Lazy>
        </ProjectProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/builder/:pageId",
    element: (
      <ProtectedRoute>
        <ProjectProvider>
          <Lazy>
            <Index />
          </Lazy>
        </ProjectProvider>
      </ProtectedRoute>
    ),
  },

  /* ---------- 404 ---------- */
  {
    path: "*",
    element: (
      <Lazy>
        <NotFound />
      </Lazy>
    ),
  },
];
